import type { JSX } from 'react';
import { useState } from 'react';
import { AboutDialog } from './AboutDialog';
import { signOut } from '../data/session';
import { ADMIN_PATH, SETTINGS_PATH, type View } from '../routes';

interface Props {
  readonly open: boolean;
  readonly view: View;
  readonly onChangeView: (view: View) => void;
  /** Administration is an owner's view. A clinician is not offered it. */
  readonly showAdministration: boolean;
}

/** The id the header's menu button points its `aria-controls` at. */
export const NAV_ID = 'app-nav';

/**
 * The app's own navigation: where things go, as opposed to the patient list,
 * which is what you are reading. New sections belong in `SECTIONS`.
 *
 * Hidden, it slides off to the left and the page reflows into the space. It
 * stays mounted so the slide can animate, and is `inert` while hidden so its
 * links drop out of the tab order instead of taking focus somewhere offscreen.
 */
export function NavSidebar({ open, view, onChangeView, showAdministration }: Props): JSX.Element {
  const [aboutOpen, setAboutOpen] = useState(false);

  const sections: readonly (readonly [View, string, string])[] = [
    ['caseload', '/', 'Caseload'],
    ...(showAdministration ? ([['administration', ADMIN_PATH, 'Administration']] as const) : []),
    ['settings', SETTINGS_PATH, 'Settings'],
  ];

  return (
    <nav id={NAV_ID} className={`nav${open ? '' : ' nav--hidden'}`} aria-label="Main" inert={!open}>
      {/* Links rather than buttons: each view has its own address, so a
          middle-click or "open in new tab" does what it says. A plain click
          is handled in place, without a reload. */}
      <ul className="nav__list">
        {sections.map(([name, href, label]) => (
          <li key={name}>
            <a
              href={href}
              className={`nav__item${view === name ? ' nav__item--on' : ''}`}
              aria-current={view === name ? 'page' : undefined}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                onChangeView(name);
              }}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>

      {/* Not places, so they sit apart from the sections above. */}
      <ul className="nav__list nav__foot">
        <li>
          <button type="button" className="nav__item" onClick={() => setAboutOpen(true)}>
            About
          </button>
        </li>
        <li>
          <button type="button" className="nav__item" onClick={signOut}>
            Sign out
          </button>
        </li>
      </ul>

      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </nav>
  );
}
